import React, { useCallback, useState } from 'react';
import {
  Body1,
  Tab,
  TabList,
  Title2,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { usePdmAssignmentStyles } from '../styles/PdmAssignmentPage.styles';
import { PartnersTab } from './PdmAssignment/PartnersTab';
import { HistoryTab } from './PdmAssignment/HistoryTab';
import { useAppRoles } from '../hooks/useAppRoles';
import { PDM_WRITE_ROLES } from '../constants/appRoles';
import { trackEvent } from '../services/telemetry';

type PdmTab = 'partners' | 'history';

export const PdmAssignmentPage: React.FC = () => {
  const styles = usePdmAssignmentStyles();
  const { hasAnyRole } = useAppRoles();
  const canEdit = hasAnyRole(PDM_WRITE_ROLES);

  const [selectedTab, setSelectedTab] = useState<PdmTab>('partners');

  const toasterId = useId('pdm-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const notify = useCallback(
    (message: string, intent: 'success' | 'error') => {
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, timeout: 4000 },
      );
    },
    [dispatchToast],
  );

  const onTabSelect = useCallback((_e: SelectTabEvent, data: SelectTabData) => {
    const value = data.value as PdmTab;
    setSelectedTab(value);
    trackEvent('pdm_tab_switch', { tab: value });
  }, []);

  return (
    <div className={styles.pageRoot}>
      <div className={styles.header}>
        <Title2>PDM assignment</Title2>
        <Body1>
          Review which people are assigned to each partner across the PDM, PDM manager, and other
          roles, and track every change in the History tab.
        </Body1>
      </div>

      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
        <Tab value="partners">Partners</Tab>
        <Tab value="history">History</Tab>
      </TabList>

      <div style={{ display: selectedTab === 'partners' ? 'contents' : 'none' }}>
        <PartnersTab notify={notify} canEdit={canEdit} />
      </div>
      <div style={{ display: selectedTab === 'history' ? 'contents' : 'none' }}>
        <HistoryTab notify={notify} active={selectedTab === 'history'} />
      </div>

      <Toaster toasterId={toasterId} position="bottom-end" />
    </div>
  );
};
